import Header from "./components/Header";
import AccountSection from "./components/AccountSection";
import GreyLink from "./components/GreyLink";
import BookingPreview from "./components/BookingPreview";
import SplitColumns from "./components/SplitColumns";
import Empty from "../components/Empty";
import AppContext from "../../../context";
import {useContext, useEffect, useState} from "react";
import Data from "../Data";
import Meta from "../../components/Meta";
import { Link } from "react-router-dom";

const getEventTime = (booking) => {
    const time = new Date(booking.Event?.date).getTime();
    return Number.isNaN(time) ? 0 : time;
};

const sortBookingsByEventDate = (bookings) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    const upcoming = bookings
        .filter(booking => getEventTime(booking) >= todayTime)
        .sort((a, b) => getEventTime(a) - getEventTime(b));
    const past = bookings
        .filter(booking => getEventTime(booking) < todayTime)
        .sort((a, b) => getEventTime(b) - getEventTime(a));

    return [...upcoming, ...past];
};

const formatWalletBalance = (domain, amount) => {
    if (domain?.businessType === 'hybrid' || domain?.businessType === 'hybrid-new') {
        return `${Number(amount || 0)} token${Number(amount || 0) === 1 ? '' : 's'}`;
    }
    return `£${((Number(amount || 0)) / 100).toFixed(2)}`;
};

const Account = () => {
    const context = useContext(AppContext);

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(async () => {
        try {
            const params = new URLSearchParams(window.location.search);
            const success = params.get('success') === 'true';
            const paymentStatus = params.get('paymentStatus');
            const transactionId = params.get('transactionId');
            const bulkReference = params.get('bulk');

            if(paymentStatus === 'success' && transactionId) {
                // Worldpay bulk return: transactionId is the groupReference (experience_bulk_...)
                try { await Data.verifyWorldpayBulkExperienceBookings(transactionId, transactionId); } catch(_) {}
                localStorage.removeItem('experience-cart');
            } else if(success) {
                const keyPrefix = 'pendingExperienceBulkCheckoutId-';
                const pendingKey = Object.keys(localStorage).find(key => key.startsWith(keyPrefix));
                const checkoutId = pendingKey ? localStorage.getItem(pendingKey) : null;
                if(checkoutId) {
                    await Data.verifySumupBulkExperienceBookings(checkoutId);
                    localStorage.removeItem(pendingKey);
                } else if(bulkReference?.startsWith('experience_bulk_')) {
                    // Stripe bulk return: the success webhook may not have landed yet, so
                    // actively confirm rather than trusting the redirect - recheck-payment
                    // resolves the whole bulk group from any one booking id in it.
                    const firstBookingId = bulkReference.replace('experience_bulk_', '');
                    try { await Data.recheckExperienceBookingPayment(firstBookingId); } catch(_) {}
                }
                localStorage.removeItem('experience-cart');
            }
            const bookings = await Data.fetchAllExperienceBookings()
            setBookings(sortBookingsByEventDate(bookings.data))
        } catch(error){
            setError(true)
        } finally {
            setLoading(false)
        }
    }, []);

    return (
        <>
            <Meta title={`My Account`} />
            <Header heading={`Hi, ${context.user.firstName}!`} subHeading={context.user.email} button={
                <div className={"flex gap-2 flex-wrap justify-end"}>
                    {context.domain.walletEnabled ? <GreyLink to={"/account/wallet"} label={"Wallet"} /> : null}
                    <GreyLink to={"/log-out"} label={"Log Out"} />
                </div>
            } />

            <div className={"container mx-auto p-6 pt-0 grid grid-cols-1 gap-4"}>
                {context.domain.walletEnabled ? (
                    <AccountSection>
                        <h3>Wallet</h3>
                        <SplitColumns>
                            <p>Current Balance</p>
                            <p>{formatWalletBalance(context.domain, context.user.walletBalance)}</p>
                            <p>Low Balance Threshold</p>
                            <p>{formatWalletBalance(context.domain, context.domain.walletLowBalanceThreshold)}</p>
                        </SplitColumns>
                        <Link to={"/account/wallet"} className={"inline-block mt-4 bg-backgroundGrey px-3 py-2 rounded-md text-center"}>View Wallet</Link>
                    </AccountSection>
                ) : null}
                <AccountSection>
                    <h3>Bookings</h3>
                    {bookings.length === 0 ?
                        <Empty>You haven't made any bookings yet.</Empty>
                    :
                        <div className={"grid grid-cols-1 gap-4 lg:grid-cols-2"}>
                            {bookings.map(booking => <BookingPreview key={booking._id} booking={booking} />)}
                        </div>
                    }

                </AccountSection>
                <div className={"grid grid-cols-1 lg:grid-cols-2 gap-4"}>
                    <AccountSection>
                        <h3>My Details</h3>
                        <SplitColumns>
                            <p>First Name</p>
                            <p>{context.user.firstName}</p>
                            <p>Last Name</p>
                            <p>{context.user.lastName}</p>
                            <p>Email Address</p>
                            <p>{context.user.email}</p>
                            <p>Phone Number</p>
                            <p>{context.user.phoneNumber}</p>
                        </SplitColumns>
                        <GreyLink to={"/account/update-details"} label={"Update Details"}/>
                    </AccountSection>
                    <AccountSection>
                        <h3>Account Security</h3>
                        <p className={"text-textGrey"}>Update your password below.</p>
                        <GreyLink to={"/account/change-password"} label={"Change Password"} />
                    </AccountSection>
                </div>
            </div>
        </>
    )
}

export default Account
